import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Checkbox } from '../../ui/checkbox';
import { Search, ChevronDown, X, AlertCircle } from 'lucide-react';
import { getLotsInRange } from '../../../utils/formUtils';

const LotsSection = ({
  formData,
  handleInputChange,
  errors,
  setErrors,
  planNumberLots,
  buildingsData,
  selectedBuildings,
  setSelectedBuildings,
  expandedBuildings,
  setExpandedBuildings,
  showBuildingDropdown,
  setShowBuildingDropdown,
  buildingSearchTerms,
  setBuildingSearchTerms,
  buildingDropdownRef,
  handleBuildingSelection,
  toggleBuildingExpansion,
  handleBuildingSearch,
  getFilteredLots,
  handleSelectAllBuilding,
  isDark
}) => {
  const [localBuildingSearchTerms, setLocalBuildingSearchTerms] = useState(buildingSearchTerms || {});

  // Sync local state with parent state
  useEffect(() => {
    setLocalBuildingSearchTerms(buildingSearchTerms || {});
  }, [buildingSearchTerms]);

  const handleLocalBuildingSearch = (buildingId, searchTerm) => {
    const newTerms = { ...localBuildingSearchTerms, [buildingId]: searchTerm };
    setLocalBuildingSearchTerms(newTerms);
    handleBuildingSearch(buildingId, searchTerm);
  };

  const getFilteredLotsLocal = (building) => {
    const searchTerm = localBuildingSearchTerms[building.id] || '';
    if (!searchTerm.trim()) {
      return building.lots;
    }
    return building.lots.filter(lot => 
      lot.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const sectionRef = useRef(null);
  const [colCount, setColCount] = useState(4);
  const [rowHeight, setRowHeight] = useState(null);

  useEffect(() => {
    setColCount(4);
    setRowHeight(null);
  }, [planNumberLots]);

  useLayoutEffect(() => {
    if (!sectionRef.current) return;
    const cards = Array.from(sectionRef.current.querySelectorAll('[data-lot-card]'));
    if (cards.length === 0) return;

    // Step 1: if still at 4 cols and any card is tall, switch to 3 and re-measure
    if (colCount === 4 && cards.some(c => c.offsetHeight > 72)) {
      setColCount(3);
      return;
    }

    // Step 2: at the final column count, lock all rows to the tallest card height
    const maxHeight = Math.max(...cards.map(c => c.offsetHeight));
    setRowHeight(maxHeight);
  }, [planNumberLots, colCount, expandedBuildings]);

  const lotsGridCols = `grid grid-cols-2 ${colCount === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-4'} gap-[12px] sm:gap-[16px]`;
  const gridStyle = rowHeight ? { gridAutoRows: `${rowHeight}px` } : undefined;

  const formatLotLabel = (lot) => {
    let label = `Lot ${lot.lot_number}`;
    if (lot.unit_number) label += ` \u2022 Unit ${lot.unit_number}`;
    if (lot.name) label += ` \u2022 ${lot.name}`;
    return label;
  };

  // Handle lot number validation for enter_lots mode
  const handleLotsSeparatedChange = (value, setErrors) => {
    const trimmedValue = value.trim();

    // Always update the value first
    handleInputChange('lots_sprated', trimmedValue);

    if (trimmedValue) {
      const lots = trimmedValue.split(',').map(lot => lot.trim()).filter(lot => lot !== '');
      const invalidLots = lots.filter(lot =>
        lot && !planNumberLots.some(validLot => validLot.lot_number === lot)
      );

      if (invalidLots.length > 0 && setErrors) {
        setErrors(prev => ({
          ...prev,
          lots_sprated: `Invalid lot numbers: ${invalidLots.join(', ')}. Please enter valid lot numbers that exist for this plan number.`
        }));
      } else if (setErrors) {
        setErrors(prev => ({
          ...prev,
          lots_sprated: ''
        }));
      }
    } else if (setErrors) {
      setErrors(prev => ({
        ...prev,
        lots_sprated: true
      }));
    }
  };

  return (
    <div
      ref={sectionRef}
      className={`border-none rounded-[10px] p-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] w-full max-w-[822px] flex flex-col gap-[16px] transition-colors duration-200 ${
        isDark ? 'bg-[#000000]' : 'bg-white'
      } ${errors.lots_sprated || errors.lotRangeStart || errors.lotRangeEnd || errors.selectedLots ? 'border-[#FF0000]' : ''}`}
    >
      {/* Header */}
      <div className={`text-[16px] font-semibold transition-colors duration-200 ${
        isDark ? 'text-white' : 'text-[#072837]'
      }`}>
        Lots
      </div>

      {/* Selection Type Radio Buttons */}
      <RadioGroup
        value={formData.lotsSelectionType}
        onValueChange={(value) => handleInputChange('lotsSelectionType', value)}
        className="flex flex-col sm:flex-row gap-3 sm:gap-[16px]"
      >
        <div className={`flex items-center gap-[8px] transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>
          <RadioGroupItem value="enter_lots" id="lotsSelectionType-enter_lots" />
          <label 
            htmlFor="lotsSelectionType-enter_lots" 
            className={`text-[16px] font-normal cursor-pointer transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}
          >
            Enter Lots
          </label>
        </div>
        <div className={`flex items-center gap-[8px] transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>
          <RadioGroupItem value="range_of_lots" id="lotsSelectionType-range_of_lots" />
          <label 
            htmlFor="lotsSelectionType-range_of_lots" 
            className={`text-[16px] font-normal cursor-pointer transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}
          >
            Select Range of lots
          </label>
        </div>
        <div className={`flex items-center gap-[8px] transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>
          <RadioGroupItem value="show_lots" id="lotsSelectionType-show_lots" />
          <label 
            htmlFor="lotsSelectionType-show_lots" 
            className={`text-[16px] font-normal cursor-pointer transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}
          >
            Show Lots
          </label>
        </div>
      </RadioGroup>

      {/* Enter Lots Mode */}
      {formData.lotsSelectionType === 'enter_lots' && (
        <div className="flex flex-col gap-[8px]">
          <input
            className={`w-full px-[12px] py-[12px] rounded-[8px] border-none text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] transition-colors duration-200 ${
              isDark
                ? 'bg-[#161515] text-white placeholder:text-[#b3b3b3]'
                : 'bg-[#f5f5f5] text-[#072837] placeholder:text-[#686868]'
            }`}
            type="text"
            placeholder="Enter Lots separated by commas example: 1, 2A, 3B ..."
            value={formData.lots_sprated || ''}
            onChange={(e) => handleLotsSeparatedChange(e.target.value, setErrors)}
          />
          {errors.lots_sprated && (
            <div className="flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
              <AlertCircle className="w-[16px] h-[16px] mt-[2px]" />
              <span>{typeof errors.lots_sprated === 'string'
                ? errors.lots_sprated
                : 'You are required to enter lot numbers'}</span>
            </div>
          )}
        </div>
      )}

      {/* Range of Lots Mode */}
      {formData.lotsSelectionType === 'range_of_lots' && (
        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-[8px]">
            <input
              className={`w-full sm:flex-1 px-[12px] py-[12px] rounded-[8px] border-none text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] transition-colors duration-200 ${
                isDark
                  ? 'bg-[#161515] text-white placeholder:text-[#b3b3b3]'
                  : 'bg-[#f5f5f5] text-[#072837] placeholder:text-[#686868]'
              }`}
              type="text"
              placeholder="Start (e.g 1, 1A, B)"
              value={formData.lotRangeStart || ''}
              onChange={(e) => handleInputChange('lotRangeStart', e.target.value)}
            />
            <input
              className={`w-full sm:flex-1 px-[12px] py-[12px] rounded-[8px] border-none text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] transition-colors duration-200 ${
                isDark
                  ? 'bg-[#161515] text-white placeholder:text-[#b3b3b3]'
                  : 'bg-[#f5f5f5] text-[#072837] placeholder:text-[#686868]'
              }`}
              type="text"
              placeholder="End (e.g 2, 2A, C)"
              value={formData.lotRangeEnd || ''}
              onChange={(e) => handleInputChange('lotRangeEnd', e.target.value)}
            />
          </div>
          {formData.lotRangeStart && formData.lotRangeEnd && (
            <div>
              <div className={`text-[14px] mb-[4px] transition-colors duration-200 ${
                isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
              }`}>Lots in range:</div>
              <div className="flex flex-wrap gap-[6px]">
                {(() => {
                  const { lots: lotsInRange, error } = getLotsInRange(planNumberLots, formData.lotRangeStart, formData.lotRangeEnd);

                  if (error === 'invalid') {
                    return <span className={`text-[14px] text-[#FF0000]`}>Invalid lot range</span>;
                  }

                  if (error === 'order') {
                    return <span className={`text-[14px] text-[#FF0000]`}>Start lot must come before end lot</span>;
                  }

                  return lotsInRange
                    .map(lot => (
                      <span key={lot.lot_number} className={`text-[14px] transition-colors duration-200 ${
                        isDark ? 'text-white' : 'text-[#072837]'
                      }`}>
                        {formatLotLabel(lot)}
                      </span>
                    ));
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Show Lots Mode */}
      {formData.lotsSelectionType === 'show_lots' && (
        <div className="flex flex-col gap-[16px]">
          {/* Building Selection Dropdown */}
          {buildingsData.buildings && buildingsData.buildings.length > 0 && (
            <div>
              <div className={`text-[16px] font-semibold mb-[9px] transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>
                Select building(s)
              </div>
              <div className="relative" ref={buildingDropdownRef}>
                <div
                  className={`${
                    isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5]'
                  } rounded-[8px] px-[12px] py-[12px] flex items-center justify-between cursor-pointer transition-colors duration-200`}
                  onClick={() => setShowBuildingDropdown(!showBuildingDropdown)}
                >
                  <span className={`text-[16px] transition-colors duration-200 ${
                    selectedBuildings.length > 0 
                      ? isDark ? 'text-white' : 'text-[#072837]'
                      : isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                  }`}>
                    {selectedBuildings.length > 0 ? `${selectedBuildings.length} Selected` : 'Select buildings'}
                  </span>
                  <ChevronDown 
                    className={`w-[12px] h-[12px] transition-transform transition-colors duration-200 ${
                      showBuildingDropdown ? 'rotate-180' : ''
                    } ${
                      buildingsData.buildings && buildingsData.buildings.length > 0 
                        ? isDark ? 'text-white' : 'text-[#000]'
                        : isDark ? 'text-[#8c8c8c]' : 'text-[#898989]'
                    }`}
                  />
                </div>
                
                {showBuildingDropdown && buildingsData.buildings && (
                  <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto border-solid border-[1px] rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
                    isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#ced4da]'
                  }`}>
                    {buildingsData.buildings.map((building) => {
                      const isSelected = selectedBuildings.includes(building.id);
                      const lotRange = building.customRange
                        ? building.customRange
                        : building.lots.length > 0
                          ? (building.lots.length === 1
                              ? `Lot # ${building.lots[0]}`
                              : `Lot # ${building.lots[0]}-${building.lots[building.lots.length - 1]}`
                            )
                          : '';

                      return (
                        <div 
                          key={building.id}
                          className={`px-3 py-2 cursor-pointer border-b last:border-b-0 group transition-colors duration-200 ${
                            isSelected
                              ? isDark 
                                ? 'bg-[#cd6d2c] text-[#000000]' 
                                : 'bg-[#e9ecef] text-[#072837]'
                              : isDark 
                                ? 'bg-[#161515] hover:bg-[#292929] text-white' 
                                : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                          }`}
                          onClick={() => handleBuildingSelection(building.id)}
                        >
                          <div className="flex items-center justify-between gap-[8px]">
                            <div className="flex items-center gap-[8px]">
                              <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  id={`building-${building.id}`}
                                  checked={isSelected}
                                  onCheckedChange={() => {
                                    handleBuildingSelection(building.id);
                                  }}
                                  isDark={isDark}
                                />
                              </div>
                              <span className={`text-[14px] font-medium transition-colors duration-200 ${
                                isSelected
                                  ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                                  : isDark ? 'text-white group-hover:!text-white' : 'group-hover:!text-white text-[#072837]'
                              }`}>{building.name}</span>
                            </div>
                            <span className={`text-[14px] transition-colors duration-200 ${
                              isSelected
                                ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                                : isDark ? 'text-[#b3b3b3] group-hover:!text-white' : 'group-hover:!text-white text-[#6c757d]'
                            }`}>
                              {lotRange}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Selected Buildings Tags */}
          {selectedBuildings.length > 0 && (
            <div className="flex flex-wrap gap-[6px]">
              {selectedBuildings.map((buildingId) => {
                const building = buildingsData.buildings?.find(b => b.id === buildingId);
                return building ? (
                  <div 
                    key={buildingId}
                    className={`bg-[#cd6d2c] flex items-center gap-[10px] px-[12px] py-[6px] rounded-[40px] transition-colors duration-200 ${
                      isDark ? 'hover:bg-[#e37f3d]' : 'hover:bg-[#b35d24]'
                    }`}
                  >
                    <span className={`text-[16px] font-normal transition-colors duration-200 ${
                      isDark ? 'text-[#000000]' : 'text-white'
                    }`}>{building.name}</span>
                    <button 
                      type="button"
                      className="p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuildingSelection(buildingId);
                      }}
                      style={{ width: '12px', height: '12px' }}
                    >
                      <X className={`w-[12px] h-[12px] transition-colors duration-200 ${
                        isDark ? 'text-[#000000]' : 'text-white'
                      }`} />
                    </button>
                  </div>
                ) : null;
              })}
            </div>
          )}

          {/* Buildings with Expandable Lots */}
          {buildingsData.buildings && buildingsData.buildings.length > 0 && (
            <div className="flex flex-col gap-[6px]">
              {buildingsData.buildings.map((building) => {
                const isExpanded = expandedBuildings[building.id];
                const allLotsSelected = building.lots.every(lot => formData.selectedLots.includes(lot));
                
                return (
                  <div className={`rounded-[8px] overflow-hidden transition-colors duration-200 ${
                    isExpanded 
                      ? `border-solid border-[1px] ${isDark ? 'border-[#292929]' : 'border-[#d9d9d9]'}`
                      : 'border-none'
                  }`}>
                    {/* Building Header */}
                    <div 
                      className={`px-[12px] py-[10px] flex justify-between items-center cursor-pointer transition-colors duration-200 ${
                        isExpanded 
                          ? isDark ? 'bg-[#161515]' : 'bg-[#fff5eb]'
                          : isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5]'
                      }`}
                      onClick={() => toggleBuildingExpansion(building.id)}
                    >
                      <span className={`text-[16px] font-medium transition-colors duration-200 ${
                        isExpanded 
                          ? isDark ? 'text-white' : 'text-[#072837]'
                          : isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                      }`}>{building.name}</span>
                      <div 
                        className="flex items-center justify-center"
                        style={{ 
                          width: '14px', 
                          height: '14px',
                          transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}
                      >
                        <ChevronDown className={`w-[12px] h-[12px] transition-colors duration-200 ${
                          isDark ? 'text-white' : 'text-black'
                        }`} />
                      </div>
                    </div>
                    
                    {/* Expanded Building Content */}
                    {isExpanded && (
                      <div className={`p-[20px] transition-colors duration-200 ${
                        isDark ? 'bg-[#000000]' : 'bg-white'
                      }`}>
                        {/* Search and Select All */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 mb-[16px]">
                          <div className="relative flex-1 w-full sm:max-w-[471px]">
                            <div className={`flex gap-[10px] items-center px-[16px] py-[6px] border-none rounded-[12px] h-[40px] transition-colors duration-200 focus-within:border-solid focus-within:border-[1px] focus-within:border-[#CD6D2C] ${
                              isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5]'
                            }`}>
                              <Search className={`w-[16px] h-[16px] shrink-0 transition-colors duration-200 ${
                                isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                              }`} />
                              <input
                                type="text"
                                className={`flex-1 bg-transparent border-0 outline-none text-[16px] transition-colors duration-200 ${
                                  isDark
                                    ? 'text-white placeholder:text-[#b3b3b3]'
                                    : 'text-[#686868] placeholder:text-[#686868]'
                                }`}
                                placeholder="Search"
                                value={localBuildingSearchTerms[building.id] || ''}
                                onChange={(e) => handleLocalBuildingSearch(building.id, e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-[8px] shrink-0">
                            <Checkbox
                              id={`select-all-${building.id}`}
                              checked={allLotsSelected}
                              onCheckedChange={() => handleSelectAllBuilding(building.id)}
                              isDark={isDark}
                            />
                            <label
                              htmlFor={`select-all-${building.id}`}
                              className={`text-[16px] font-medium cursor-pointer whitespace-nowrap transition-colors duration-200 ${
                                isDark ? 'text-white' : 'text-[#072837]'
                              }`}
                            >
                              Select All
                            </label>
                          </div>
                        </div>
                        
                        {/* Lots Grid */}
                        <div className={lotsGridCols} style={gridStyle}>
                          {(() => {
                            const filteredLots = getFilteredLotsLocal(building).slice().sort((a, b) => {
                              const lotA = planNumberLots.find(l => l.lot_number === a);
                              const lotB = planNumberLots.find(l => l.lot_number === b);
                              return String(lotA?.lot_number ?? a).localeCompare(
                                String(lotB?.lot_number ?? b),
                                undefined,
                                { numeric: true, sensitivity: 'base' }
                              );
                            });
                            return filteredLots.map((lotNum) => {
                              const lotObj = planNumberLots.find(l => l.lot_number === lotNum) || { lot_number: lotNum };
                              return (
                                <div
                                  key={lotNum}
                                  data-lot-card
                                  className={`flex items-start gap-[8px] rounded-[5px] px-[6px] py-[8px] w-full transition-colors duration-200 ${
                                    isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5]'
                                  }`}
                                >
                                  <Checkbox
                                    id={`lot-${building.id}-${lotNum}`}
                                    checked={formData.selectedLots.includes(lotNum)}
                                    onCheckedChange={() => handleInputChange('selectedLots', lotNum)}
                                    isDark={isDark}
                                    className="mt-[3px] shrink-0"
                                  />
                                  <label htmlFor={`lot-${building.id}-${lotNum}`} className="flex flex-col cursor-pointer">
                                    <span className={`text-[14px] font-normal leading-snug transition-colors duration-200 ${
                                      isDark ? 'text-white' : 'text-[#072837]'
                                    }`}>
                                      {`Lot ${lotObj.lot_number}${lotObj.unit_number ? ` \u2022 Unit ${lotObj.unit_number}` : ''}`}
                                    </span>
                                    {lotObj.name && (
                                      <span className={`text-[13px] transition-colors duration-200 ${
                                        isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                                      }`}>
                                        {lotObj.name}
                                      </span>
                                    )}
                                  </label>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Fallback: Simple Lots Display (if no buildings data) */}
          {!buildingsData.buildings && (
             <div className={lotsGridCols} style={gridStyle}>
              {[...planNumberLots].sort((a, b) =>
                String(a.lot_number ?? '').localeCompare(
                  String(b.lot_number ?? ''),
                  undefined,
                  { numeric: true, sensitivity: 'base' }
                )
              ).map((lot) => (
                <div key={lot.lot_number} data-lot-card className={`flex items-start gap-[8px] rounded-[5px] px-[6px] py-[8px] transition-colors duration-200 ${
                  isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5]'
                }`}>
                  <Checkbox
                    id={`selectedLots-${lot.lot_number}`}
                    checked={formData.selectedLots.includes(lot.lot_number)}
                    onCheckedChange={() => handleInputChange('selectedLots', lot.lot_number)}
                    isDark={isDark}
                    className="mt-[3px] shrink-0"
                  />
                  <label htmlFor={`selectedLots-${lot.lot_number}`} className="flex flex-col cursor-pointer">
                    <span className={`text-[14px] font-normal leading-snug transition-colors duration-200 ${
                      isDark ? 'text-white' : 'text-[#072837]'
                    }`}>
                      {`Lot ${lot.lot_number}${lot.unit_number ? ` \u2022 Unit ${lot.unit_number}` : ''}`}
                    </span>
                    {lot.name && (
                      <span className={`text-[13px] transition-colors duration-200 ${
                        isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                      }`}>
                        {lot.name}
                      </span>
                    )}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error Messages */}
      {formData.lotsSelectionType === 'range_of_lots' && (errors.lotRangeStart || errors.lotRangeEnd) && (
        <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
          <AlertCircle className="w-[16px] h-[16px]" />
          <span>You are required to enter both start and end lot numbers</span>
        </div>
      )}

      {formData.lotsSelectionType === 'show_lots' && errors.selectedLots && (
        <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
          <AlertCircle className="w-[16px] h-[16px]" />
          <span>You are required to select at least one lot</span>
        </div>
      )}
    </div>
  );
};

export default LotsSection;

